// Deterministic, auditable narrative-to-civilian suggestions.
// A rule may only emit language supported by an explicit text signal. Rules
// intentionally avoid credentials, scope, metrics, outcomes, and seniority.

const RULES = [
  {
    id: 'inventory-accountability',
    signals: [/accountab/i, /inventory/i, /asset location/i, /verified? balances?/i, /storage/i, /inspection/i, /\bissue[ds]?\b/i],
    skills: ['Inventory accountability', 'Inventory control', 'Asset tracking'],
    titles: ['Inventory Control Specialist', 'Material Coordinator'],
    keywords: ['inventory accountability', 'asset location', 'stock balance', 'storage', 'inspection', 'material issue'],
    bullet: 'Supported inventory accountability, inventory control, and asset-tracking activities.',
  },
  {
    id: 'aircraft-parts',
    signals: [/aircraft parts?/i, /parts store/i, /c-?17/i, /maintenance support/i],
    skills: ['Aircraft parts support', 'Aviation logistics support'],
    titles: ['Aviation Parts Specialist', 'Aircraft Material Coordinator'],
    keywords: ['aircraft parts', 'aviation logistics', 'maintenance support', 'parts store'],
    bullet: 'Supported aircraft-parts operations for aviation maintenance requirements.',
  },
  {
    id: 'part-research',
    signals: [/national stock numbers?/i, /\bnsns?\b/i, /part numbers?/i, /catalog research/i],
    skills: ['Stock-number and part-number research', 'Material identification'],
    titles: ['Parts Research Specialist', 'Material Identification Specialist'],
    keywords: ['NSN research', 'part-number research', 'material identification', 'catalog research'],
    bullet: 'Researched national stock numbers and part numbers to identify requested material and support accurate fulfillment.',
  },
  {
    id: 'requisitions',
    signals: [/customer requests?/i, /initiated? orders?/i, /backorders?/i, /requisitions?/i, /processed? requests?/i],
    skills: ['Requisition processing', 'Order and backorder coordination', 'Customer request fulfillment'],
    titles: ['Supply Support Specialist', 'Order Fulfillment Coordinator'],
    keywords: ['requisitions', 'backorders', 'order processing', 'customer requests', 'fulfillment'],
    bullet: 'Supported customer-request, order, and backorder processing for material requirements.',
  },
  {
    id: 'deployment-equipment',
    signals: [/deployment readiness/i, /deployment bags?/i, /mobility bags?/i, /deployment equipment/i],
    skills: ['Deployment-equipment support', 'Readiness logistics'],
    titles: ['Readiness Logistics Coordinator', 'Deployment Equipment Specialist'],
    keywords: ['deployment readiness', 'deployment equipment', 'readiness logistics'],
    bullet: 'Supported deployment readiness and deployment-equipment operations.',
  },
  {
    id: 'protective-equipment',
    signals: [/jslist/i, /chemical[- ]protective/i, /protective equipment/i, /cbrn equipment/i],
    skills: ['Protective-equipment accountability', 'Protective-equipment logistics'],
    titles: ['Protective Equipment Coordinator'],
    keywords: ['JSLIST', 'protective equipment', 'equipment accountability'],
    bullet: 'Supported chemical-protective equipment accountability and logistics activities.',
  },
  {
    id: 'prioritization',
    signals: [/prioriti[sz]ed? requirements?/i, /urgent requirements?/i, /mission priorit/i],
    skills: ['Requirements prioritization', 'Material requirements coordination'],
    titles: ['Material Requirements Coordinator'],
    keywords: ['requirements prioritization', 'material requirements', 'supply coordination'],
    bullet: 'Prioritized material requirements to support customer and maintenance needs.',
  },
  {
    id: 'discrepancies',
    signals: [/supply discrepancies?/i, /inventory discrepancies?/i, /resolved? discrepancies?/i, /reconcil/i],
    skills: ['Supply discrepancy resolution', 'Inventory reconciliation'],
    titles: ['Inventory Reconciliation Specialist'],
    keywords: ['supply discrepancy', 'inventory reconciliation', 'issue resolution'],
    bullet: 'Researched and resolved supply discrepancies to support accurate material records and customer requests.',
  },
  {
    id: 'maintainer-support',
    signals: [/coordinated? with maintainers?/i, /maintainers?/i, /maintenance teams?/i],
    skills: ['Cross-functional maintenance coordination', 'Internal customer support'],
    titles: ['Maintenance Supply Liaison', 'Material Support Coordinator'],
    keywords: ['maintainer support', 'cross-functional coordination', 'maintenance supply'],
    bullet: 'Coordinated with maintainers to clarify, prioritize, and support material requirements.',
  },
  {
    id: 'asset-status',
    signals: [/asset[- ]status/i, /status information/i, /order status/i, /provided? status/i],
    skills: ['Asset-status reporting', 'Supply status communication'],
    titles: ['Supply Status Coordinator'],
    keywords: ['asset status', 'order status', 'status reporting', 'customer communication'],
    bullet: 'Provided asset-status information to customers and cross-functional partners.',
  },
];

const unique = values => [...new Set(values.filter(Boolean))];

export function buildNarrativeSuggestions({ bio = '', occupation = null } = {}) {
  const narrative = bio.trim();
  if (!narrative) return { skills: [], titles: [], keywords: [], bullets: [], matchedThemes: [] };

  const matched = RULES.filter(rule => rule.signals.some(signal => signal.test(narrative)));
  const specialtyText = `${occupation?.code || ''} ${occupation?.title || ''}`;
  const isSupplySpecialty = /2S0X1|materiel management|material management/i.test(specialtyText);

  const baseline = isSupplySpecialty ? {
    skills: ['Supply support'],
    titles: ['Supply Chain Specialist'],
    keywords: ['materiel management', 'supply support'],
  } : { skills: [], titles: [], keywords: [] };

  return {
    skills: unique([...baseline.skills, ...matched.flatMap(rule => rule.skills)]),
    titles: unique([...baseline.titles, ...matched.flatMap(rule => rule.titles)]),
    keywords: unique([...baseline.keywords, ...matched.flatMap(rule => rule.keywords)]),
    bullets: unique(matched.map(rule => rule.bullet)),
    matchedThemes: matched.map(rule => rule.id),
  };
}

export { RULES as narrativeTranslationRules };
